import React, { useEffect, useState } from 'react';
import {
  Button,
  Card,
  CardBody,
  CardHeader,
  Col,
  Container,
  FormGroup,
  Input,
  Label,
  Row,
} from 'reactstrap';
import config from 'config';
import {
  getContent,
  updateContentFiles,
} from 'features/contents/contentsAPI';
import { useAlertError, useAlertPlus } from 'hooks';
import AlertError from 'components/AlertError';
import AlertModal from 'components/AlertModal';
import { getErrorMessage } from 'utils/errors';
import { Link } from 'react-router-dom';

const CONTENT_KEY = 'minimum_ethics_banner';

interface BannerData {
  image: string;
  url: string;
}

function parseBanner(raw: string): BannerData {
  try {
    const data = JSON.parse(raw) as Partial<BannerData>;
    return {
      image: data.image || '',
      url: data.url || '',
    };
  } catch {
    return { image: '', url: '' };
  }
}

function buildPublicUrl(relativePath: string): string {
  if (!relativePath) {
    return '';
  }
  return `${config.baseUrl}/${encodeURI(relativePath)}`;
}

export default function MinimumEthicsBanner() {
  const [currentImage, setCurrentImage] = useState('');
  const [currentUrl, setCurrentUrl] = useState('');
  const [imageFile, setImageFile] = useState<File | null>(null);
  const [pdfFile, setPdfFile] = useState<File | null>(null);
  const [imagePreview, setImagePreview] = useState('');
  const [saving, setSaving] = useState(false);

  const [showError, messageError, detailsError, callbackError, alert] =
    useAlertError();
  const [successShow, successMessage, successCallback, successAlert] =
    useAlertPlus('');

  useEffect(() => {
    getContent(CONTENT_KEY)
      .then(result => {
        if (result) {
          const data = parseBanner(result.content);
          setCurrentImage(data.image);
          setCurrentUrl(data.url);
        }
      })
      .catch(err => {
        console.error(err);
        alert(
          'No se pudo recuperar el banner. ' +
          'Por favor, intente nuevamente más tarde',
          getErrorMessage(err),
        );
      });
  }, []);

  useEffect(() => {
    if (!imageFile) {
      setImagePreview('');
      return;
    }
    const url = URL.createObjectURL(imageFile);
    setImagePreview(url);
    return () => URL.revokeObjectURL(url);
  }, [imageFile]);

  const handleImageChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0] || null;
    if (file && !file.type.startsWith('image/')) {
      alert('El archivo seleccionado no es una imagen válida.');
      return;
    }
    setImageFile(file);
  };

  const handlePdfChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0] || null;
    if (file && file.type !== 'application/pdf') {
      alert('El archivo seleccionado no es un PDF válido.');
      return;
    }
    setPdfFile(file);
  };

  const handleSave = () => {
    if (!currentImage && !imageFile) {
      alert('Debe seleccionar una imagen para el banner.');
      return;
    }
    if (!currentUrl && !pdfFile) {
      alert('Debe seleccionar el archivo PDF del banner.');
      return;
    }

    const files: Record<string, File> = {};
    if (imageFile) files.image = imageFile;
    if (pdfFile) files.pdf = pdfFile;

    setSaving(true);
    updateContentFiles(CONTENT_KEY, files)
      .then(result => {
        setSaving(false);
        if (!result) {
          alert(
            'No se pudo guardar el banner. ' +
            'Por favor, intente nuevamente más tarde',
          );
          return;
        }
        const data = parseBanner(result.content);
        setCurrentImage(data.image);
        setCurrentUrl(data.url);
        setImageFile(null);
        setPdfFile(null);
        successAlert('El banner se guardó correctamente.');
      })
      .catch(err => {
        setSaving(false);
        console.error(err);
        alert(
          'No se pudo guardar el banner. ' +
          'Por favor, intente nuevamente más tarde',
          getErrorMessage(err),
        );
      });
  };

  const previewSrc = imagePreview || buildPublicUrl(currentImage);
  const pdfHref = buildPublicUrl(currentUrl);

  return (
    <>
      <div className="header bg-gradient-info pb-8 pt-5 pt-md-8">
        <Container fluid>
          <div className="header-body">
          </div>
        </Container>
      </div>
      <Container className="mt--7" fluid>
        <Row>
          <Col>
            <Card className="shadow">
              <CardHeader className="border-0">
                <Row className="align-items-center">
                  <Col md='8'>
                    <h3 className="mb-0">
                      Banner del Mínimo Ético
                    </h3>
                  </Col>
                  <Col md='4' className='text-right'>
                    <Button
                      color='primary'
                      className='btn btn-md'
                      onClick={handleSave}
                      disabled={saving}
                    >
                      {saving ? 'Guardando...' : 'Guardar'}
                    </Button>
                    <Link
                      to="/admin/contenidos"
                      onClick={event => {
                        if (saving) {
                          event.preventDefault();
                        }
                      }}
                    >
                      <Button
                        color='secondary'
                        className='btn btn-md'
                        disabled={saving}
                      >
                        Volver
                      </Button>
                    </Link>
                  </Col>
                </Row>
              </CardHeader>
              <CardBody>
                <FormGroup>
                  <Label for='banner-image'>Imagen</Label>
                  <Input
                    id='banner-image'
                    type='file'
                    accept='image/*'
                    onChange={handleImageChange}
                    disabled={saving}
                  />
                  {previewSrc && (
                    <div className='mt-3'>
                      <img
                        src={previewSrc}
                        alt='Banner del Mínimo Ético'
                        style={{ maxWidth: '100%', maxHeight: '300px' }}
                      />
                    </div>
                  )}
                </FormGroup>
                <FormGroup>
                  <Label for='banner-pdf'>Archivo PDF</Label>
                  <Input
                    id='banner-pdf'
                    type='file'
                    accept='application/pdf'
                    onChange={handlePdfChange}
                    disabled={saving}
                  />
                  {pdfFile ? (
                    <div className='mt-2 text-muted'>
                      Archivo seleccionado: {pdfFile.name}
                    </div>
                  ) : (
                    pdfHref && (
                      <div className='mt-2'>
                        <a
                          href={pdfHref}
                          target='_blank'
                          rel='noopener noreferrer'
                        >
                          Ver archivo actual
                        </a>
                      </div>
                    )
                  )}
                </FormGroup>
              </CardBody>
            </Card>
          </Col>
        </Row>
      </Container>
      <AlertError
        isOpen={showError}
        message={messageError}
        details={detailsError}
        onClose={callbackError}
      />
      <AlertModal
        isOpen={successShow}
        message={successMessage}
        onClose={successCallback}
      />
    </>
  );
}
